import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { CheckCircle, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { primaryLedgerService } from '../../services/primaryLedgerService';
import { useToast } from '../ui/use-toast';
import { ValidationResult, SpreadsheetValidation } from '../../types/primaryLedger';

interface AddCompanyFormProps {
  onCompanyAdded: () => void;
  userId: string;
}

const AddCompanyForm: React.FC<AddCompanyFormProps> = ({ onCompanyAdded, userId }) => {
  const { toast } = useToast();
  const [companyName, setCompanyName] = useState('');
  const [salesProfitId, setSalesProfitId] = useState('');
  const [ppcResultsId, setPpcResultsId] = useState('');
  const [marginHistoryId, setMarginHistoryId] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  const allSpreadsheetsValid =
    validationResult?.salesProfit?.success &&
    validationResult?.ppcResults?.success &&
    validationResult?.marginHistory?.success;

  /**
   * Extract spreadsheet ID from Google Sheets URL or return the input if it's already an ID
   * Handles URLs like: https://docs.google.com/spreadsheets/d/{ID}/edit...
   */
  const extractSpreadsheetId = (input: string): string => {
    const trimmed = input.trim();
    
    // Regex to match Google Sheets URL and extract ID
    // Pattern: /spreadsheets/d/{SPREADSHEET_ID}/
    const urlPattern = /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/;
    const match = trimmed.match(urlPattern);
    
    if (match && match[1]) {
      return match[1]; // Return extracted ID
    }
    
    // If no match, assume it's already an ID
    return trimmed;
  };

  /**
   * Handle blur event on spreadsheet ID inputs to extract ID from URL
   */
  const handleSpreadsheetIdBlur = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const extractedId = extractSpreadsheetId(value);
    if (extractedId !== value) {
      setter(extractedId);
    }
  };

  const handleCheckAccess = async () => {
    if (!salesProfitId.trim() || !ppcResultsId.trim() || !marginHistoryId.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter all three Spreadsheet IDs',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }

    setIsChecking(true);
    setValidationResult(null);

    try {
      const result = await primaryLedgerService.validateSpreadsheets(
        salesProfitId,
        ppcResultsId,
        marginHistoryId
      );

      setValidationResult(result);

      const allValid = result.allValid;
      
      toast({
        title: allValid ? 'Validation Successful' : 'Validation Failed',
        description: allValid 
          ? 'All spreadsheets are accessible with correct permissions and tabs.'
          : 'Some spreadsheets failed validation. Please check the details below.',
        variant: allValid ? 'default' : 'destructive',
        duration: 5000
      });
    } catch (error) {
      console.error('Error validating spreadsheets:', error);
      toast({
        title: 'Validation Error',
        description: error instanceof Error ? error.message : 'Failed to validate spreadsheet access',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setIsChecking(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Company name is required',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }

    if (!allSpreadsheetsValid) {
      toast({
        title: 'Validation Required',
        description: 'Please check access to all spreadsheets and ensure they pass validation',
        variant: 'destructive',
        duration: 5000
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const companyId = await primaryLedgerService.createCompany(
        companyName,
        salesProfitId,
        ppcResultsId,
        marginHistoryId,
        userId
      );

      await primaryLedgerService.triggerManualSyncNonBlocking(companyId);

      setCompanyName('');
      setSalesProfitId('');
      setPpcResultsId('');
      setMarginHistoryId('');
      setValidationResult(null);

      toast({
        title: 'Success',
        description: `Company "${companyName}" added successfully! Initial data sync has started - check the company card below for progress.`,
        duration: 8000
      });

      onCompanyAdded();
    } catch (error) {
      console.error('Error adding company:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to add company',
        variant: 'destructive',
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const ValidationResultDisplay: React.FC<{ result: SpreadsheetValidation | null; label: string }> = ({ result, label }) => {
    if (!result) return null;

    return (
      <div className={`flex items-start gap-2 p-3 rounded-lg text-sm border ${
        result.success
          ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800'
          : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800'
      }`}>
        {result.success
          ? <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
          : <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
        }
        <div className="flex-1 space-y-1">
          <div className="font-semibold">{label}</div>
          
          {result.success && (
            <>
              {result.spreadsheetTitle && (
                <div className="text-xs text-gray-700 dark:text-gray-300">
                  ✓ Spreadsheet: <span className="font-medium">"{result.spreadsheetTitle}"</span>
                </div>
              )}
              {result.tabName && (
                <div className="text-xs text-gray-700 dark:text-gray-300">
                  ✓ Tab found: <span className="font-medium">"{result.tabName}"</span>
                </div>
              )}
            </>
          )}
          
          {!result.success && (
            <>
              {result.error && (
                <div className="text-xs font-medium mt-1">{result.error}</div>
              )}
              {result.spreadsheetTitle && (
                <div className="text-xs mt-1 text-gray-700 dark:text-gray-300">
                  Spreadsheet: "{result.spreadsheetTitle}"
                </div>
              )}
              {result.availableTabs && result.availableTabs.length > 0 && (
                <div className="text-xs mt-1 text-gray-700 dark:text-gray-300">
                  Available tabs: {result.availableTabs.join(', ')}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add New Company</CardTitle>
        <CardDescription>
          Configure Google Sheets connections for a new Primary Ledger company
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter company name"
              disabled={isChecking || isSubmitting}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="salesProfitId">Sales & Profit History ID</Label>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                <a
                  href="https://drive.google.com/drive/u/0/folders/19nOftjjA6w5sPNN_SA3mL_OeVNJ6gVHi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 inline-flex items-center gap-1"
                >
                  Find Sales & Profit History spreadsheets
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <Input
                id="salesProfitId"
                value={salesProfitId}
                onChange={(e) => setSalesProfitId(e.target.value)}
                onBlur={(e) => handleSpreadsheetIdBlur(e.target.value, setSalesProfitId)}
                placeholder="Spreadsheet ID or URL"
                disabled={isChecking || isSubmitting}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tab: "1. ASIN Sales History"
              </p>
            </div>

            <div>
              <Label htmlFor="ppcResultsId">PPC Results ID</Label>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                <a
                  href="https://drive.google.com/drive/u/0/folders/1XPMUm46MXrR2bfgnp6iEHvA-bWTZJaMI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 inline-flex items-center gap-1"
                >
                  Find PPC Results spreadsheets
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <Input
                id="ppcResultsId"
                value={ppcResultsId}
                onChange={(e) => setPpcResultsId(e.target.value)}
                onBlur={(e) => handleSpreadsheetIdBlur(e.target.value, setPpcResultsId)}
                placeholder="Spreadsheet ID or URL"
                disabled={isChecking || isSubmitting}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tab: "4. ASIN Results Ledger"
              </p>
            </div>

            <div>
              <Label htmlFor="marginHistoryId">Margin History ID</Label>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                <a
                  href="https://drive.google.com/drive/u/0/folders/1i8sLhUuurNRHS_tTGmQhsGXsNv8deoQv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 inline-flex items-center gap-1"
                >
                  Find Margin History spreadsheets
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <Input
                id="marginHistoryId"
                value={marginHistoryId}
                onChange={(e) => setMarginHistoryId(e.target.value)}
                onBlur={(e) => handleSpreadsheetIdBlur(e.target.value, setMarginHistoryId)}
                placeholder="Spreadsheet ID or URL"
                disabled={isChecking || isSubmitting}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tab: "Margin Ledger"
              </p>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleCheckAccess}
            disabled={isChecking || isSubmitting}
            className="w-full md:w-auto"
          >
            {isChecking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking Access...
              </>
            ) : (
              'Check Access'
            )}
          </Button>

          {validationResult && (
            <div className="space-y-3">
              <div className="space-y-2">
                <ValidationResultDisplay result={validationResult.salesProfit} label="Sales & Profit History" />
                <ValidationResultDisplay result={validationResult.ppcResults} label="PPC Results" />
                <ValidationResultDisplay result={validationResult.marginHistory} label="Margin History" />
              </div>
              
              {validationResult.serviceAccountEmail && (
                <div className="text-xs text-gray-600 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                  <span className="font-medium">Service Account:</span> {validationResult.serviceAccountEmail}
                </div>
              )}
              
              {!validationResult.allValid && (
                <Alert variant="destructive" className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-medium mb-1">Validation failed. Common issues:</div>
                    <ul className="list-disc list-inside text-xs space-y-1">
                      <li>Service account may not have Editor access to the spreadsheet(s)</li>
                      <li>Tab names may not match exactly (check spelling and case)</li>
                      <li>Spreadsheet ID may be incorrect</li>
                    </ul>
                    <div className="mt-2 text-xs">
                      Share each spreadsheet with: <code className="bg-red-100 dark:bg-red-900 px-1 py-0.5 rounded">{validationResult.serviceAccountEmail || 'jls-apps@jls-apps.iam.gserviceaccount.com'}</code>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={!allSpreadsheetsValid || isSubmitting || isChecking}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding Company...
                </>
              ) : (
                'Add Company'
              )}
            </Button>
          </div>

          {!allSpreadsheetsValid && validationResult && (
            <Alert variant="destructive">
              <AlertDescription>
                Please ensure all spreadsheets pass validation before adding the company.
              </AlertDescription>
            </Alert>
          )}
        </form>
      </CardContent>
    </Card>
  );
};

export default AddCompanyForm;